import { ChangeDetectionStrategy, Component, ElementRef, inject, OnDestroy, OnInit } from '@angular/core'

import { FloatoverService } from './floatover.service'
import { takeUntil } from 'rxjs/operators'
import { Subject } from 'rxjs'

@Component({
  selector: 'app-floatover',
  standalone: true,
  imports: [],
  templateUrl: './floatover.component.html',
  styleUrls: ['./floatover.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class FloatoverComponent implements OnInit, OnDestroy {
  private floatoverService = inject(FloatoverService)
  private elRef = inject(ElementRef)

  showView: boolean = false
  inputHtml: string = ''
  loading: boolean = true
  private TAG = 'FloatoverComponent'

  private unsubscribe = new Subject<void>()
  private resizeObserver: ResizeObserver | null = null

  styleTop: number = 0
  styleLeft: number = 0

  ngOnInit() {
    this.floatoverService
      .getFloatoverShowStatus()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((show) => {
        if (show) {
          this.setShow(true)
        } else {
          this.setShow(false)
        }
      })

    this.floatoverService
      .getContentHtml()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((content) => {
        this.inputHtml = content
      })

    this.floatoverService
      .getLoading()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((loading) => {
        this.loading = loading
      })

    this.floatoverService
      .getPosition()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(({ x, y }) => {
        this.setPosition(x, y)
      })
  }

  ngOnDestroy(): void {
    this.unsubscribe.next()
    this.unsubscribe.complete()

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
  }

  private setPosition(x: number, y: number) {
    this.styleTop = y - 20
    this.styleLeft = x - 250

    this.observeElementSize(x, y)
  }

  private observeElementSize(mouseX: number, mouseY: number) {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect

        // Only reposition if we have valid dimensions
        if (width > 0 && height > 0) {
          this.repositionAfterVisible(mouseX, mouseY, width, height)
          // Disconnect after first successful resize
          this.resizeObserver?.disconnect()
          break
        }
      }
    })

    // Start observing the floatover container
    const floatoverElement = this.elRef.nativeElement.querySelector('.floatover-container')
    if (floatoverElement) {
      this.resizeObserver.observe(floatoverElement)
    }
  }

  private repositionAfterVisible(mouseX: number, mouseY: number, width: number, height: number) {
    // Reposition based on actual dimensions
    // Center horizontally on mouse cursor, position above it
    this.styleLeft = mouseX - width / 2
    this.styleTop = mouseY - height - 10

    // Ensure the floatover doesn't go off-screen
    this.styleLeft = Math.max(10, Math.min(this.styleLeft, window.innerWidth - width - 10))
    this.styleTop = Math.max(10, Math.min(this.styleTop, window.innerHeight - height - 10))
  }

  private setShow(b: boolean) {
    this.showView = b
  }
}
